import {
  EmailAttachmentRead,
  InvoiceDetailRead,
  InvoiceRejectionCreate,
  InvoicesAllRead,
  InvoiceService,
  InvoiceStatistics,
  InvoiceStatusChangeRead,
  OpenAPI,
  StatusGroup,
  SettingsService,
  InvoiceLineSave,
  InvoiceType,
} from "../api/generated"
import axios, { AxiosResponse } from "axios"
import { convertDateToYyyyMmDd } from "../util/formatDate"
import { calculateComplexityFromLineItems } from "../util/calculateComplexity"
import { InvoiceQueueFilters } from "../components/InvoicesTable/InvoicesTable"

OpenAPI.BASE = import.meta.env.VITE_API_ENDPOINT
export async function getInvoiceQueue(StatusGroup:StatusGroup,filters:InvoiceQueueFilters ): Promise<Array<InvoicesAllRead>> {
  let data = await InvoiceService.getAllInvoicesInvoicesAllGet(
    StatusGroup,
    filters.pageNum,
    filters.limit,
    filters.sortBy,
    filters.sortOrder,
    filters.accessToken,
    filters.arrowId,
    filters.invoiceNumber,
    filters.assignedUserEmail,
    filters.supplierName,
    filters.supplierSite,
    filters.currency,
    filters.queueName,
    filters.businessUnit,
    filters.invoiceAge,
    filters.invoiceStartDate,
    filters.invoiceEndDate,
    filters.glStartDate,
    filters.glEndDate,
    filters.invoiceAmountMin,
    filters.invoiceAmountMax,
  )
  return data;
}
export async function getInvoiceFiltersDrpDowns(statusGroup:StatusGroup){
  let data = await InvoiceService.getFilterDropdownInvoicesFilterDropdownGet(statusGroup)
  return data
}

export async function getInvoiceStatistics(
  queueId: number,filters:InvoiceQueueFilters
): Promise<InvoiceStatistics> {
  let data =
    await InvoiceService.getInvoiceStatisticsInvoicesInvoiceStatisticsGet(
      queueId,
      filters.accessToken,
      filters.arrowId,
      filters.invoiceNumber,
      filters.assignedUserEmail,
      filters.supplierName,
      filters.supplierSite,
      filters.currency,
      filters.queueName,
      filters.businessUnit,
      filters.invoiceAge,
      filters.invoiceStartDate,
      filters.invoiceEndDate,
      filters.glStartDate,
      filters.glEndDate,
      filters.invoiceAmountMin,
      filters.invoiceAmountMax,
    )
  return data
}

export async function checkoutInvoice(
  invoiceId: number,
): Promise<InvoiceDetailRead> {
  let data = await InvoiceService.getInvoiceDetailInvoicesIdCheckOutPost(
    invoiceId,
  ) 
  return data
}

export async function invoiceHistory(
  invoiceId: number,
): Promise<InvoiceStatusChangeRead[]> {
  let data = await InvoiceService.getInvoiceHistoryInvoicesIdHistoryGet(
    invoiceId,
  )
  return data
}

export async function invoiceQueues(justCurrentUser: boolean) {
  let data = await SettingsService.getWorkqueuesAllSettingsWorkqueuesAllGet(
    justCurrentUser,
  )
  return data
}

export async function moveInvoice(invoiceId: number, queueId: number) {
  let data = await InvoiceService.patchStatusInvoicesIdStatusPatch(invoiceId, {
    queue_id: queueId,
  })
  return data
}

export async function getInvoiceByIds(invoicesArr:number[]) {
  let data = await InvoiceService.getInvoicesByIdsInvoicesByIdsPost({
    ids: invoicesArr
  })
  return data;
}

export interface SaveInvoiceArgs {
  invoiceId: number
  glDate: string
  noteText?: string
  invoice_user_flag?: number
  currency?: string
  invoiceDate?: string
  externalInvoiceId?: string
  receiverNameInvoice?: string
  netAmount?: number
  totalAmount?: number
  totalTaxAmount?: number
  invoiceLinesItems: InvoiceLineSave[]
  submit: boolean
  skipValidation: boolean
  supplierSiteId?: number
  invoiceSupplierName?: string
  invoiceType: InvoiceType,
  supplierBankDetailsId?: number,
  supplierIban: string
  supplierBicSwift: string
  supplierBankAccountNumber: string
  supplierSortCode: string
  qrIban: string
  qrIbanReference: string
  isQrr: boolean,
  spotRates: (Record<string, string> | null),
  nonPoRequester: string,
}

export async function saveInvoice(invoiceDetail: SaveInvoiceArgs) {
  const requestBody = {
    complete_and_validate: invoiceDetail.submit,
    gl_date: convertDateToYyyyMmDd(invoiceDetail.glDate),
    note_text: invoiceDetail.noteText,
    invoice_user_flag: invoiceDetail.invoice_user_flag,
    invoice_item_contents: {
      invoice_type: invoiceDetail.invoiceType,
      invoice_id: invoiceDetail.invoiceId,
      currency: invoiceDetail.currency,
      complexity: calculateComplexityFromLineItems(invoiceDetail.invoiceLinesItems),
      invoice_date: convertDateToYyyyMmDd(invoiceDetail.invoiceDate),
      external_invoice_id: invoiceDetail.externalInvoiceId,
      net_amount: invoiceDetail.netAmount,
      total_amount: invoiceDetail.totalAmount,
      total_tax_amount: invoiceDetail.totalTaxAmount,
      invoice_lines_items: invoiceDetail.invoiceLinesItems,
      supplier_site_id: invoiceDetail.supplierSiteId,
      supplier_name: invoiceDetail.invoiceSupplierName,
      receiver_name: invoiceDetail.receiverNameInvoice,
      supplier_bank_details_id: invoiceDetail.supplierBankDetailsId,
      qr_iban: invoiceDetail.qrIban,
      qr_iban_reference: invoiceDetail.qrIbanReference,
      is_qrr: invoiceDetail.isQrr,
      spot_rates: invoiceDetail.spotRates,
      non_po_requester: invoiceDetail.nonPoRequester,
    },
  }

  if (invoiceDetail.skipValidation) {
    let data =
      await InvoiceService.completeNoValidateInvoiceInvoicesIdCompleteNoValidationPost(
        invoiceDetail.invoiceId,
        requestBody,
      )
    return data
  } else {
    let data = await InvoiceService.completeInvoiceInvoicesIdCompletePost(
      invoiceDetail.invoiceId,
      requestBody,
    )
    return data
  }
}

export async function moveInvoices(invoiceIds: number[], queueId: number) {
  let data = await InvoiceService.patchStatusesInvoicesStatusesPatch({
    ids: invoiceIds,
    invoice_status_patch: {
      queue_id: queueId,
    },
  })
  return data
}

export async function unassignInvoices(invoiceIds: number[]) {
  let data = await InvoiceService.patchStatusesInvoicesStatusesPatch({
    ids: invoiceIds,
    invoice_status_patch: {
      user_id: null,
    },
  })
  return data
}

export async function getEmailAttachments(
  emailId: number,
): Promise<EmailAttachmentRead[]> {
  let data =
    await InvoiceService.getEmailAttachmentsInvoicesEmailAttachmentsGet(emailId)
  return data
}

export async function rejectInvoice(
  ids: number[],
  rejectionArgs: InvoiceRejectionCreate,
) {
  let data = await InvoiceService.rejectInvoiceBatchInvoicesRejectBatchPost({
    ids: ids,
    rejection_reasons: rejectionArgs,
  })
  return data
}

export type PDFData = ArrayBuffer
export const getPDFFromGCP = async (signedUrl: string): Promise<PDFData> => {
  try {
    const response: AxiosResponse<PDFData> = await axios.get(signedUrl, {
      responseType: "arraybuffer",
    })
    const pdfData: PDFData = response.data

    return pdfData
  } catch (error) {
    console.error("Error fetching PDF:", error)
    // Handle error appropriately
    throw error // You might want to throw the error to be caught by the caller
  }
}
